import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppConfig } from '@mkp/shared/util-app-config';
import { getMessageError, isHttpStatusError } from '@mkp/shared/data-access';
import { SplashscreenService } from '@mkp/shared/util-splashscreen';
import { AccountResource, addLegalEntityToAccountDto } from '@mkp/account/data-access';
import { selectAccountById } from '@mkp/account/state';
import {
  authActions,
  authWorkspaceActions,
  authWorkspaceGuardActions,
  boAuthActions,
} from '@mkp/auth/actions';
import { LegalEntityResource } from '@mkp/legal-entity/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { selectIsBoUser, selectLoggedInUser } from '@mkp/user/state-logged-in/selectors';
import { of } from 'rxjs';
import { catchError, delay, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { isBoUserFn } from './helpers/auth-effects.helpers';
import { NavigationTrackerService } from './helpers/navigation-tracking.service';

export const handleMultipleWorkspaceUser = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        authActions.loadAccountsAfterLoginSuccess,
        authActions.loadAccountsAfterBOLoginSuccess
      ),
      map(({ accounts }) =>
        accounts.length > 1
          ? authWorkspaceActions.workspaceSelectionRequired()
          : authWorkspaceActions.workspaceSelectionNotRequired()
      )
    ),
  { functional: true }
);

// TODO: Remove when auth init changes to first load accounts for all types of users
export const setBoUserWorkspaceSelectingStateOnInit = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(authActions.loadActiveAccountMembershipsSuccess),
      concatLatestFrom(() => store.select(selectIsBoUser)),
      filter(([, isBoUser]) => isBoUserFn(isBoUser)),
      map(() => authWorkspaceActions.workspaceSelectionRequired())
    ),
  { functional: true }
);

export const fetchAndSetAccount = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    accountResource = inject(AccountResource),
    legalEntityResource = inject(LegalEntityResource),
    router = inject(Router)
  ) =>
    actions$.pipe(
      ofType(
        authWorkspaceGuardActions.workspaceSelectedFromUrlResource,
        boAuthActions.loadWorkspace
      ),
      concatLatestFrom(({ accountId }) => store.select(selectAccountById(accountId))),
      switchMap(([{ accountId, type }, accountFromStore]) =>
        accountFromStore
          ? of(authActions.workspaceFoundInStore({ accounts: [accountFromStore] }))
          : accountResource.getById(accountId).pipe(
              addLegalEntityToAccountDto(legalEntityResource),
              map((account) => authActions.loadWorkspaceAccountSuccess({ accounts: [account] })),
              catchError((error: unknown) => {
                // error-case: redirect to dashboard only for bo-user, the guard has it's own redirect
                if (type === boAuthActions.loadWorkspace.type) {
                  router.navigate([`/${AppConfig.routes.dashboard}`]);
                }

                return of(
                  authActions.loadWorkspaceAccountError({
                    error: getMessageError(error, 'fetchAndSetAccount'),
                    status: isHttpStatusError(error) ? error.status : 500,
                  })
                );
              })
            )
      )
    ),
  { functional: true }
);

export const skipWorkspaceSelectionIfRouteIsNotGuarded = createEffect(
  (actions$ = inject(Actions), navigationTrackingService = inject(NavigationTrackerService)) =>
    actions$.pipe(
      ofType(authWorkspaceActions.workspaceSelectionRequired),
      switchMap(() => navigationTrackingService.isUnguardedNavigationEnd$),
      map(() => authWorkspaceActions.unguardedRoute())
    ),
  { functional: true }
);

export const hideSplashScreen = createEffect(
  (actions$ = inject(Actions), store = inject(Store), splashscreen = inject(SplashscreenService)) =>
    actions$.pipe(
      ofType(
        authActions.unverifiedUserAuthenticated,
        authActions.workspaceSelected,
        authActions.loadActiveAccountMembershipsFailed,
        authActions.workspaceFoundInStore,
        authActions.loadWorkspaceCompanySuccess,
        authActions.loadWorkspaceCompanyError,
        authActions.loadWorkspaceAccountError,
        boAuthActions.loadWorkspaceError,
        authWorkspaceActions.workspaceSelectionNotRequired,
        authWorkspaceActions.unguardedRoute,
        authWorkspaceGuardActions.redirectedOnNoSelectedWorkspace,
        authWorkspaceGuardActions.workspaceSelectionNotRequired,
        authWorkspaceGuardActions.workspaceSelectedFromUrlResource
      ),
      take(1),
      concatLatestFrom(() => store.select(selectLoggedInUser).pipe(filter(Boolean))),
      delay(500),
      take(1),
      filter(() => splashscreen.isShown()),
      tap(() => splashscreen.hide())
    ),
  { functional: true, dispatch: false }
);
